import { type KeyboardEvent, useEffect, useMemo, useRef } from 'react';

import {
  SearchInput,
  type SearchInputProps,
} from '@/app/components/forms/search-input';
import { Icon } from '@/app/components/icons';
import { IconLocationOn } from '@/app/components/icons/maps/icon-location-on';
import { IconChevronRight } from '@/app/components/icons/ui-actions/icon-chevron-right';
import {
  DealerEmptyState,
  EMPTY_STATE_DATA,
} from '@/app/components/shared/dealer/components/shared/empty-state';
import { InfiniteScrollList } from '@/app/components/shared/dealer/components/shared/infinite-scroll-list';
import { useDealerSearchKey } from '@/app/components/shared/dealer/hooks/useDealerSearchKey';
import styles from '@/app/components/shared/dealer/styles.module.css';
import {
  DEALER_DEFAULT_VALUE,
  DEALER_TYPES,
  THAILAND_LOCATIONS,
} from '@/app/components/shared/dealer/utils/dealer-constant';
import { ICON_DEALER_TYPE_MAP } from '@/app/components/shared/dealer/utils/dealer-icons';
import { searchSuggestPlaces } from '@/app/components/shared/dealer/utils/dealer-search';
import { createDealerNameFormat } from '@/app/components/shared/dealer/utils/format';
import { DealerPlace } from '@/app/components/shared/dealer/utils/types';
import { DEALER_PLACEHOLDER_SEARCH } from '@/app/constants/dealer';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { safeCall } from '@/app/utils/safeCall';
import { type DealerGrouping } from '@/services/contentstack/models/dealer';

const MAX_LENGTH = 3;
const GROUP_NAME = 'components/shared/dealer/shared/smart-search/search-box';

export interface SearchBoxProps
  extends Omit<SearchInputProps, 'name' | 'value'> {
  variant?: 'default' | 'list';
  searchWrapperClassName?: string;
  name?: string;
  items?: DealerGrouping[];
  fullScreenEmptyState?: boolean;
  hideAutoComplete?: boolean;
  value: string;
  onClose?: () => void;
  onEnter?: (value: string) => void;
  onChangeSearch?: (value: string, isFromParams: boolean) => void;
  onClickSuggestPlace?: (value: DealerPlace) => void;
  onClickSearchResult?: (value: DealerGrouping, type: string) => void;
  onClear?: () => void;
}

interface SuggestPlaceProps {
  name: string;
  onClick: () => void;
}

interface ResultPlaceProps {
  name: string;
  address: string;
  icon: string;
  onClick: () => void;
}

const baseClassName =
  'hover:bg-silver-100 flex cursor-pointer items-center gap-x-2 px-4';

const SuggestPlace = ({ name, onClick, ...props }: SuggestPlaceProps) => {
  return (
    <li
      className={cn(baseClassName, 'w-full py-2')}
      onClick={onClick}
      {...props}
    >
      <Icon className="size-5 text-red-500" icon={<IconLocationOn />} />
      <span
        title={name ?? ''}
        className="text-body-1-fixed overflow-hidden text-ellipsis whitespace-nowrap text-gray-700"
      >
        {name ?? ''}
      </span>
    </li>
  );
};

const ResultPlace = ({
  name,
  address,
  onClick,
  icon,
  ...props
}: ResultPlaceProps) => {
  const renderIcon = useMemo(() => {
    const dealerType = DEALER_TYPES.find((item) => item.id === icon);
    return dealerType?.icon || 'IconStore';
  }, [icon]);

  return (
    <li
      className={cn(baseClassName, 'h-fit py-3')}
      onClick={onClick}
      {...props}
    >
      <Icon
        className="mr-2 size-5 text-red-500"
        icon={ICON_DEALER_TYPE_MAP[renderIcon]}
      />
      <div>
        <p
          className="text-body-2-fixed font-noto-bold where-span:text-inline-wrap"
          dangerouslySetInnerHTML={{
            __html: createDealerNameFormat(name),
          }}
        />
        <span className="text-caption line-clamp-1 text-gray-600">
          {address || DEALER_DEFAULT_VALUE.ADDRESS}
        </span>
      </div>
      <Icon className="ml-auto text-gray-800" icon={<IconChevronRight />} />
    </li>
  );
};

export const SearchBox = ({
  variant = 'default',
  searchWrapperClassName,
  name = 'search-dealer',
  value,
  items = [],
  fullScreenEmptyState = false,
  hideAutoComplete = false,
  onClickSuggestPlace,
  onClickSearchResult,
  onChangeSearch,
  onClose,
  onEnter,
  onClear,
  ...props
}: SearchBoxProps) => {
  const { removeTextSearchParams } = useDealerSearchKey();
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (variant !== 'default') return;
    const requestId = requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    });

    return () => cancelAnimationFrame(requestId);
  }, [variant]);

  const handleChange = (value: string, isFromParams = false) => {
    const allWhiteSpaces = value.length > 0 && value.trim().length === 0;
    if (allWhiteSpaces) {
      return;
    }
    safeCall(onChangeSearch, value, isFromParams);
  };

  const isNotAcceptSearch = !value || value.trim().length < MAX_LENGTH;

  const searchData = useMemo(() => {
    if (items?.length === 0 || isNotAcceptSearch) return [];

    return items;
  }, [items, isNotAcceptSearch]);

  const suggestPlaces = useMemo(() => {
    if (isNotAcceptSearch) {
      return [];
    }
    return searchSuggestPlaces(THAILAND_LOCATIONS, value) || [];
  }, [value, isNotAcceptSearch]);

  const handleSuggestPlaceClick = (item: DealerPlace) => {
    safeCall(onClose);
    safeCall(onClickSuggestPlace, item);
  };

  const handleSearchResultClick = (item: DealerGrouping, type: string) => {
    safeCall(onClose);
    safeCall(onClickSearchResult, item, type);
  };

  const handleEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (value.length < MAX_LENGTH || event.key !== 'Enter') return;
    safeCall(onEnter, value);
    safeCall(onClose);
  };

  const handleClear = () => {
    removeTextSearchParams('', false);
    safeCall(onClear);
  };

  const showSuggestPlaces = !!value && suggestPlaces.length > 0;

  const listClassName = cn('flex-col space-y-2', styles.xlSearchResultPanel);
  const defaultClassName = cn(
    'flex-col xl:flex-row xl:justify-between xl:gap-x-4 xl:px-10 xl:py-4',
    styles.searchResultsContainer
  );

  return (
    <>
      <div className={searchWrapperClassName}>
        <SearchInput
          {...props}
          inputRef={inputRef}
          placeholder={DEALER_PLACEHOLDER_SEARCH}
          inputClassName="w-full"
          name={name}
          onChange={handleChange}
          value={value}
          maxLength={50}
          onKeyDown={handleEnter}
          onClear={handleClear}
          {...createDataAttributes({
            test: 'search-input',
          })}
        />
      </div>

      {!hideAutoComplete && (
        <>
          {searchData?.length > 0 || suggestPlaces.length > 0 ? (
            <div
              className={cn('flex h-full', {
                [listClassName]: variant === 'list',
                [defaultClassName]: variant === 'default',
                'pb-10 xl:pb-0':
                  variant === 'default' && suggestPlaces.length > 0,
              })}
            >
              {!!showSuggestPlaces && (
                <ul
                  className={cn({
                    'w-full': searchData.length === 0,
                  })}
                >
                  {[...suggestPlaces].slice(0, 3).map((item, index) => (
                    <SuggestPlace
                      key={index}
                      name={item.name}
                      onClick={() => handleSuggestPlaceClick(item)}
                      {...createDataAttributes({
                        test: `suggest-place-${index}`,
                      })}
                      {...createDataGA({
                        click: true,
                        eventName: 'dealer-search',
                        group: GROUP_NAME,
                        value: `${item.lat},${item.lng}`,
                        text: item.name,
                      })}
                    />
                  ))}
                </ul>
              )}

              {searchData.length > 0 && (
                <div className="flex h-full min-h-0 w-full flex-col">
                  <p
                    className="text-body-2-fixed font-noto-bold flex-shrink-0 px-4 py-1"
                    {...createDataAttributes({
                      test: 'title-search-result',
                    })}
                  >
                    ผลการค้นหา {`(${searchData.length})`}
                  </p>

                  <ul className="relative min-h-0 flex-1 overflow-y-auto">
                    <InfiniteScrollList
                      items={searchData}
                      renderItem={(item: DealerGrouping, index) => (
                        <ResultPlace
                          key={item?.groupForSearch?.id || index}
                          name={`${item?.groupForSearch?.name ?? ''}`}
                          address={item?.groupForSearch?.address ?? ''}
                          icon={item?.groupForSearch?.type ?? ''}
                          onClick={() =>
                            handleSearchResultClick(
                              item,
                              item.groupForSearch?.type || ''
                            )
                          }
                          {...createDataAttributes({
                            test: `result-place-${index}`,
                          })}
                          {...createDataGA({
                            click: true,
                            eventName: 'dealer-search',
                            group: GROUP_NAME,
                            value: item.uid,
                            text: item?.groupForSearch?.name,
                          })}
                        />
                      )}
                    />
                  </ul>
                </div>
              )}
            </div>
          ) : (
            value.length >= MAX_LENGTH && (
              <>
                {variant === 'list' && (
                  <p
                    className="text-body-2-fixed px-4 py-3"
                    {...createDataAttributes({ test: 'title-search-result' })}
                  >
                    ผลการค้นหา {`(${searchData.length})`}
                  </p>
                )}
                <DealerEmptyState
                  className={cn({
                    'mx-4 mb-7': !fullScreenEmptyState,
                    'absolute top-24 left-0 z-50 mt-5 w-full rounded-none':
                      fullScreenEmptyState,
                  })}
                  title={
                    variant === 'list' ? EMPTY_STATE_DATA.titleBySearch : ''
                  }
                  fullScreen={variant === 'list'}
                />
              </>
            )
          )}
        </>
      )}
    </>
  );
};
